import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Flask } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { fmtQty } from "../lib/utils";
import { BrutalButton, BrutalInput } from "./Brutal";

const TARGET_TYPES = ["cream", "shampoo", "nasal"];

// v414.15 — producer conversion dialog: N vials -> M creams/shampoos/nasal
// bottles under a custom name. Shown to distributors with can_convert and to
// elevated users (own stock, or a producer's stock from their detail page).
// v414.16 — Recipe mode: pick a recipe (AXIS I 15ml jar), enter jars made,
// and every ingredient's vials are auto-deducted by the per-jar math.
export const ConvertStockDialog = ({ product, holderId, onClose, onConverted }) => {
  const { t } = useTranslation();
  const holder = holderId || product.owner_id;
  const [recipes, setRecipes] = useState([]);
  const [mode, setMode] = useState("manual");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/recipes", { params: { holder_id: holder } })
      .then(({ data }) => {
        const relevant = (data || []).filter((r) =>
          (r.ingredients || []).some((i) => i.product_id === product.id));
        setRecipes(relevant);
        if (relevant.length > 0) setMode("recipe");
      })
      .catch(() => {});
  }, [holder, product.id]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="convert-stock-dialog"
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md space-y-3">
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <Flask size={20} weight="bold" className="text-[#8a6a1c]" />
          {t("convert.title", { name: product.name })}
        </h2>
        <p className="text-xs text-zinc-500">{t("convert.hint", { stock: fmtQty(product.current_stock) })}</p>
        {recipes.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {["recipe", "manual"].map((m) => (
              <button key={m} type="button" onClick={() => setMode(m)}
                data-testid={`convert-mode-${m}`}
                className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                  mode === m ? "bg-[#101012] text-[#c9a24a] border-[#c9a24a]"
                    : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                }`}>
                {t(m === "recipe" ? "convert.mode_recipe" : "convert.mode_manual")}
              </button>
            ))}
          </div>
        )}
        {mode === "recipe" && recipes.length > 0 ? (
          <RecipeForm recipes={recipes} holderId={holder} busy={busy} setBusy={setBusy}
            onClose={onClose} onConverted={onConverted} />
        ) : (
          <ManualForm product={product} busy={busy} setBusy={setBusy}
            onClose={onClose} onConverted={onConverted} />
        )}
      </div>
    </div>
  );
};

function RecipeForm({ recipes, holderId, busy, setBusy, onClose, onConverted }) {
  const { t } = useTranslation();
  const [recipeId, setRecipeId] = useState(recipes[0].id);
  const [qty, setQty] = useState(1);
  const recipe = recipes.find((r) => r.id === recipeId) || recipes[0];
  const n = Number(qty);
  const valid = Number.isInteger(n) && n >= 1;

  const lines = useMemo(() => (recipe.ingredients || []).map((ing) => {
    const needed = valid ? +(ing.per_unit * n).toFixed(2) : 0;
    return { ...ing, needed, short: needed > (ing.available || 0) + 1e-9 };
  }), [recipe, n, valid]);
  const anyShort = lines.some((l) => l.short || !l.product_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error(t("convert.form_invalid")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.id}/produce`, {
        output_quantity: n, holder_id: holderId,
      });
      toast.success(t("convert.done_toast", {
        qty: n, unit: data.target_product?.unit || "", name: data.target_product?.name || recipe.output_name,
      }));
      onConverted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="convert-recipe-form">
      {recipes.length > 1 && (
        <div>
          <label className="block text-xs font-semibold mb-1">{t("convert.recipe_label")}</label>
          <select value={recipeId} onChange={(e) => setRecipeId(e.target.value)}
            data-testid="convert-recipe-select"
            className="w-full h-10 border border-zinc-300 rounded-lg px-2 text-sm bg-white">
            {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      )}
      <div className="rounded-lg border border-[#c9a24a] bg-[#fbf6e7]/60 p-3">
        <p className="text-sm font-semibold" data-testid="convert-recipe-name">{recipe.name}</p>
        {recipe.batch_note && (
          <p className="text-[11px] text-zinc-500 mt-0.5">{t("convert.recipe_batch", { note: recipe.batch_note })}</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold mb-1">{t("convert.recipe_qty_label", { unit: recipe.output_unit })}</label>
        <BrutalInput type="number" min="1" step="1" value={qty}
          onChange={(e) => setQty(e.target.value)} data-testid="convert-recipe-qty" />
      </div>
      <div className="space-y-1">
        {lines.map((l) => (
          <p key={l.match} data-testid={`convert-ing-${l.match}`}
            className={`text-xs ${l.short || !l.product_id ? "text-red-600 font-semibold" : "text-zinc-600"}`}>
            {t("convert.recipe_ing_line", {
              name: l.product_name || l.label,
              needed: fmtQty(l.needed),
              have: fmtQty(l.available || 0),
            })}
          </p>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500" data-testid="convert-recipe-adds">
        {t("convert.recipe_adds", { qty: valid ? n : 0, unit: recipe.output_unit, name: recipe.output_name })}
      </p>
      <div className="flex gap-3 pt-1">
        <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </BrutalButton>
        <BrutalButton type="submit" className="flex-1" disabled={busy || anyShort || !valid}
          data-testid="convert-recipe-submit">
          {busy ? t("common.saving") : t("convert.recipe_submit")}
        </BrutalButton>
      </div>
    </form>
  );
}

function ManualForm({ product, busy, setBusy, onClose, onConverted }) {
  const { t } = useTranslation();
  const [type, setType] = useState("cream");
  const [name, setName] = useState(`${product.name} Cream`);
  const [nameTouched, setNameTouched] = useState(false);
  const [qtyIn, setQtyIn] = useState(1);
  const [qtyOut, setQtyOut] = useState(1);

  const suffix = { cream: "Cream", shampoo: "Shampoo", nasal: "Nasal Mist" };
  const pickType = (tp) => {
    setType(tp);
    if (!nameTouched) setName(`${product.name} ${suffix[tp]}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    const nIn = Number(qtyIn);
    const nOut = Number(qtyOut);
    if (!Number.isInteger(nIn) || nIn < 1 || !Number.isInteger(nOut) || nOut < 1 || !name.trim()) {
      toast.error(t("convert.form_invalid"));
      return;
    }
    if (nIn > product.current_stock) {
      toast.error(t("convert.not_enough", { stock: fmtQty(product.current_stock) }));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/convert-stock`, {
        quantity: nIn, output_quantity: nOut, target_type: type, target_name: name.trim(),
      });
      toast.success(t("convert.done_toast", {
        qty: nOut, unit: data.target_product?.unit || "", name: data.target_product?.name || name.trim(),
      }));
      onConverted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="convert-manual-form">
      <div>
        <label className="block text-xs font-semibold mb-1">{t("convert.type_label")}</label>
        <div className="grid grid-cols-3 gap-2">
          {TARGET_TYPES.map((tp) => (
            <button key={tp} type="button" onClick={() => pickType(tp)}
              data-testid={`convert-type-${tp}`}
              className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${
                type === tp ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}>
              {t(`convert.type_${tp}`)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold mb-1">{t("convert.name_label")}</label>
        <BrutalInput value={name}
          onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
          data-testid="convert-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1">{t("convert.qty_in_label")}</label>
          <BrutalInput type="number" min="1" step="1" max={product.current_stock} value={qtyIn}
            onChange={(e) => setQtyIn(e.target.value)} data-testid="convert-qty-in" />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1">{t("convert.qty_out_label")}</label>
          <BrutalInput type="number" min="1" step="1" value={qtyOut}
            onChange={(e) => setQtyOut(e.target.value)} data-testid="convert-qty-out" />
        </div>
      </div>
      <p className="text-[11px] text-zinc-500" data-testid="convert-summary">
        {t("convert.summary", { qty_in: qtyIn || 0, qty_out: qtyOut || 0, name: name.trim() })}
      </p>
      <div className="flex gap-3 pt-1">
        <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </BrutalButton>
        <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="convert-submit">
          {busy ? t("common.saving") : t("convert.submit")}
        </BrutalButton>
      </div>
    </form>
  );
}
